import { useQuery } from "@tanstack/react-query";
import { optionApi } from "./axios";
import {
  DataI,
  ServiceResponseI,
  PropsOptionI,
  OptionI,
} from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

const getOptions = async ({
  pagination,
  sorting,
  globalFilter,
}: PropsOptionI): Promise<ServiceResponseI<DataI<OptionI>>> => {
  try {
    const validateToken = ValidateCurrentToken();

    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));

    //headers: { 'Authorization': validateToken
    const { data } = await optionApi.get<ServiceResponseI<DataI<OptionI>>>("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllOptions = async (): Promise<ServiceResponseI<OptionI[]>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await optionApi.get<ServiceResponseI<OptionI[]>>(
      `/all-no-pagination`,
      { headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const optionPost = async <T>(
  getForms: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await optionApi.post<ServiceResponseI<string>>(
      "",
      getForms,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const optionPut = async <T>(
  id: number,
  updateForms: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await optionApi.put<ServiceResponseI<string>>(
      `/${id}`,
      updateForms,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const optionById = async (
  id: number,
): Promise<ServiceResponseI<OptionI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await optionApi.get<ServiceResponseI<OptionI>>(`/${id}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const optionDelete = async (
  id: number,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await optionApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const assignOptionstoModule = async <T>(
  id: number,
  updateForms: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await optionApi.put<ServiceResponseI<string>>(
      `assign-options-to-module/${id}`,
      updateForms,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useOptions = ({
  sorting,
  pagination,
  globalFilter,
}: PropsOptionI) => {
  const optionsQuery = useQuery(
    ["options", { pagination, sorting, globalFilter }],
    () => getOptions({ pagination, sorting, globalFilter }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  return {
    optionsQuery,
  };
};
